import { Field, FieldProps, Formik } from 'formik';
import * as React from 'react';
import { classes, stylesheet } from 'typestyle';
import { object, string } from 'yup';

import { Button, Input, Select } from '@src/controls';
import { withFormItem } from '@src/decorators';
import { bigLabelLayout, inputStyle } from '@src/forms';
import { FormattedMessage, useFormatMessage } from '@src/i18n';

import { commons } from '@src/styles';
import { getFormikError } from '@src/utils/forms';
import { matchHexColor } from '@src/utils';
import { labelIconsOptions } from './label-icon';
import { LabelPreview } from './label-preview';
import { CategoryLabelModel } from '@src/models';
import { labelTypesOptions } from './label-options';

const InputField = withFormItem(Input, bigLabelLayout);
const SelectField = withFormItem(Select, bigLabelLayout);

interface Props {
  onSubmit: (values: AddCategoryLabelsFormValues) => void;
}

export type AddCategoryLabelsFormValues = Pick<
  CategoryLabelModel,
  'name' | 'type' | 'color' | 'icon' | 'dark_mode_color'
>;

const CreateRegionSchema = object({
  name: string().required(),
  type: string().required(),
  color: string().test('is-hex-color', 'HEX_VALUE_ERROR', value => matchHexColor(value ?? '')),
  dark_mode_color: string().test('is-hex-color', 'HEX_VALUE_ERROR', value =>
    matchHexColor(value ?? '')
  ),
});

export const AddCategoryLabelForm: React.FC<Props> = ({ onSubmit }) => {
  const formatMessage = useFormatMessage();
  return (
    <Formik<AddCategoryLabelsFormValues>
      onSubmit={values => onSubmit(values)}
      initialValues={{ name: '', type: 'LABEL_TYPE_CUSTOM', color: '', icon: '' }}
      validationSchema={CreateRegionSchema}
    >
      {({ handleSubmit, errors, touched, setFieldValue, values, isValid }) => (
        <form onSubmit={handleSubmit} className={styles.formWrapper}>
          <Field name="name">
            {({ field }: FieldProps<AddCategoryLabelsFormValues['name']>) => (
              <InputField
                {...field}
                disabled={values.type === 'LABEL_TYPE_SWAN'}
                label={<FormattedMessage id="NAME" />}
                labelAlign="left"
                error={getFormikError(touched.name, errors.name)}
                className={classes(
                  inputStyle,
                  getFormikError(touched.name, errors.name) && styles.inputError
                )}
              />
            )}
          </Field>
          <Field name="type">
            {({ field }: FieldProps<AddCategoryLabelsFormValues['type']>) => (
              <SelectField
                label={<FormattedMessage id="TYPE" />}
                options={labelTypesOptions}
                labelAlign="left"
                error={getFormikError(touched.type, errors.type)}
                {...field}
                onChange={value => {
                  if (value === 'LABEL_TYPE_SWAN') {
                    setFieldValue('type', value);
                    setFieldValue('name', 'Nordic Swan Ecolabel Delivery');
                  } else if (values.type === 'LABEL_TYPE_SWAN') {
                    // if previous value === swan label - clear input
                    setFieldValue('type', value);
                    setFieldValue('name', '');
                  } else {
                    setFieldValue('type', value);
                  }
                }}
                className={classes(inputStyle, styles.typeInput)}
              />
            )}
          </Field>
          <Field name="color">
            {({ field: { value, ...field } }: FieldProps<AddCategoryLabelsFormValues['color']>) => (
              <InputField
                {...field}
                // hide only the value for swan label while keeping it in the form state
                value={values.type === 'LABEL_TYPE_SWAN' ? '' : value}
                disabled={values.type === 'LABEL_TYPE_SWAN'}
                label={<FormattedMessage id="LABEL_FORM.LIGHT_MODE_COLOR" />}
                labelAlign="left"
                error={errors.color ? formatMessage('HEX_VALUE_ERROR') : ''}
                className={classes(
                  inputStyle,
                  getFormikError(touched.color, errors.color) && styles.inputError
                )}
              />
            )}
          </Field>
          <Field name="dark_mode_color">
            {({ field: { value, ...field } }: FieldProps<AddCategoryLabelsFormValues['color']>) => (
              <InputField
                {...field}
                // hide only the value for swan label while keeping it in the form state
                value={values.type === 'LABEL_TYPE_SWAN' ? '' : value}
                disabled={values.type === 'LABEL_TYPE_SWAN'}
                label={<FormattedMessage id="LABEL_FORM.DARK_MODE_COLOR" />}
                labelAlign="left"
                error={errors.dark_mode_color ? formatMessage('HEX_VALUE_ERROR') : ''}
                className={classes(
                  inputStyle,
                  getFormikError(touched.dark_mode_color, errors.dark_mode_color) &&
                    styles.inputError
                )}
                labelTooltip={<FormattedMessage id="LABEL_FORM.DARK_MODE_COLOR.TOOLTIP" />}
              />
            )}
          </Field>
          <Field name="icon">
            {({ field: { value, ...field } }: FieldProps<AddCategoryLabelsFormValues['icon']>) => (
              <SelectField
                label={<FormattedMessage id="ICON" />}
                options={labelIconsOptions}
                disabled={values.type === 'LABEL_TYPE_SWAN'}
                allowClear
                labelAlign="left"
                error={getFormikError(touched.icon, errors.icon)}
                {...field}
                // hide only the value for swan label while keeping it in the form state
                value={values.type === 'LABEL_TYPE_SWAN' ? '' : value}
                onChange={value => {
                  setFieldValue('icon', value);
                }}
                className={classes(inputStyle, styles.typeInput)}
              />
            )}
          </Field>
          <LabelPreview columns={[8, 16]} label={values} isValid={isValid} />
          <div className={commons.flex}>
            <Button type="primary" htmlType="submit" className={styles.submitButton}>
              <FormattedMessage id="ADD" />
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

const styles = stylesheet({
  submitButton: {
    marginLeft: 'auto',
    marginTop: '10px',
  },
  formWrapper: {
    width: '550px',
  },
  inputError: {
    border: '1px solid #f5222d',
    $nest: {
      '&:hover': {
        border: '1px solid #F5222D',
      },
    },
  },
  typeInput: {
    $nest: {
      '&.ant-select-single:not(.ant-select-customize-input) .ant-select-selector': {
        border: 'none',
        backgroundColor: '#FAF8F7',
      },
      '&.ant-select-single:not(.ant-select-customize-input) .ant-select-selector:hover': {
        backgroundColor: 'white',
      },
    },
  },
});
